import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import {
  BubbleAccordion,
  BubbleButton,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import './GroupsHome.scss';

import { useUserInfo } from '@go-client/context/UserInfoContext';
import BubbleTable, {
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import GroupsNavigation from '@go-client/components/Navigations/GroupsNavigation';
import PageWrapper from './PageWrapper';
import Page from './Page';
import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import BubbleBadge, { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import { groupResourceClient } from '@go-client/lib/api/v2/groupResource';
import GroupResourceForm, {
  GroupsResourceMode,
} from '@go-client/components/GroupBookingForm/GroupResourceForm';
import {
  BubbleTypeaheadOption,
  BubbleTypeaheadValue,
} from '@go-client/components/BubbleUI/BubbleTypeahead';
import uuid from 'uuid';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { useGroupResourceData } from '@go-client/hooks/useGroupResourceData';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import { canCreateOrEditGroupResources } from '@go-shared/lib/permissions';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

type ResourceTableItem = {
  id: string;
  name: string;
  price: number;
  resourceType: string;
  archived: boolean;
};

interface GroupsFilters {
  selectedTypes: Array<BubbleTypeaheadValue<string>>;
}

export function makeVariantFromType(type: string) {
  let variant: BubbleBadgeProps['variant'] = 'smoke';

  switch (type) {
    case 'F&B':
      variant = 'lime';
      break;
    case 'ENT':
    case 'rejected':
      variant = 'navy';
      break;
    case 'HSK':
      variant = 'banana';
      break;
    default:
      variant = 'smoke';
      break;
  }

  return variant;
}

function makeBadgeFromType(status: string) {
  const variant = makeVariantFromType(status);

  return <BubbleBadge variant={variant}>{status}</BubbleBadge>;
}

export default function ResourceLibrary(props: RouteComponentProps) {
  const { activeProperty } = useActiveProperty();

  const { user } = useUserInfo();
  const serverInfo = useServerInfo();
  const [search, setSearch] = useState('');
  const [showArchive, setShowArchive] = useState<{ [key: string]: boolean }>({});
  const [mode, setMode] = useState<GroupsResourceMode>({ type: 'default' });
  const [filters, setFilters] = useState<GroupsFilters>({ selectedTypes: [] });
  const [resourceType, setResourceType] = useState<Array<BubbleTypeaheadOption<string>>>([]);
  const [showOnlyArchived, setShowOnlyArchived] = useState(false);

  const { resourceData, loading, refresh } = useGroupResourceData(serverInfo.brand._id);

  useEffect(() => {
    const resourceTypeOptions = resourceData.resourceTypes.map(s => ({
      key: s,
      item: s,
      variant: makeVariantFromType(s),
    }));

    setResourceType(resourceTypeOptions);

    const resourceTypeMap = mapBy(resourceTypeOptions, 'key');

    setFilters({
      selectedTypes: resourceTypeOptions
        .map(sopt => {
          const refOpt = resourceTypeMap[sopt.key];
          return refOpt
            ? {
                key: uuid(),
                item: sopt.item,
                refKey: refOpt.key,
              }
            : undefined;
        })
        .filter(notUndefined),
    });
  }, [resourceData.resourceTypes]);

  const filteredResources = useMemo(() => {
    const selectedTypeKeys = new Set(filters.selectedTypes.map(t => t.item.toLowerCase()));
    const normalizedSearch = search.trim().toLowerCase();

    return resourceData.resources
      .filter(resource => {
        const typeMatch =
          selectedTypeKeys.size === 0 || selectedTypeKeys.has(resource.resourceType.toLowerCase());
        const searchMatch = resource.name.toLowerCase().includes(normalizedSearch);
        const archivedMatch = showOnlyArchived
          ? resource.archived === true
          : resource.archived === false;
        return typeMatch && searchMatch && archivedMatch;
      })
      .map(resource => ({
        id: resource._id,
        name: resource.name,
        price: resource.price,
        resourceType: resource.resourceType,
        archived: resource.archived,
      }));
  }, [resourceData.resources, filters.selectedTypes, search, showOnlyArchived]);

  const tableFields: Array<BubbleTableFields<ResourceTableItem>> = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'type',
      label: 'Type',
    },
    {
      key: 'price',
      label: 'Price',
      tdProps: { className: 'whitespace-nowrap' },
    },
    {
      key: 'actions',
      label: '',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i pr-4-i text-right text-right' },
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<ResourceTableItem>> = [
    {
      key: 'name',
      render: item => <> {item.name}</>,
    },
    {
      key: 'type',
      render: item => makeBadgeFromType(item.resourceType),
    },
    {
      key: 'price',
      render: item => <>${item.price}</>,
    },
    {
      key: 'actions',
      render: item =>
        canCreateOrEditGroupResources(user, serverInfo) && (
          <>
            <BubbleButton
              onClick={() => handleEdit(item)}
              className="p-2-i"
              variant="navy"
              inverse
              disabled={false}
              icon="edit"
            />
            <BubbleButton
              onClick={() => handleClone(item)}
              className="p-2-i"
              variant="slate"
              inverse
              disabled={false}
              icon="copy"
            />
            {!showArchive[item.id] && (
              <BubbleButton
                className="p-2-i"
                variant="dirt"
                inverse
                onClick={() => {
                  setShowArchive({ ...showArchive, [item.id]: true });
                }}
                icon="archive"
              />
            )}
            {showArchive[item.id] && (
              <>
                <BubbleButton
                  className="p-2-i"
                  variant="crimson"
                  inverse
                  onClick={() => handleArchive(item)}
                  icon={findIconDefinition({
                    prefix: 'fak',
                    iconName: 'solid-box-archive-circle-check',
                  })}
                />
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowArchive({ ...showArchive, [item.id]: false });
                  }}
                  icon={findIconDefinition({
                    prefix: 'fak',
                    iconName: 'solid-box-archive-circle-xmark',
                  })}
                />
              </>
            )}
          </>
        ),
    },
  ];

  async function handleArchive(item: ResourceTableItem) {
    await groupResourceClient.archive({
      params: { resourceId: item.id },
      body: { archived: !item.archived },
    });
    refresh();
  }

  function handleEdit(item: {
    id: string;
    name: string;
    price: number;
    resourceType: string;
  }): void {
    setMode({ type: 'edit', data: item });
  }

  function handleClone(item: {
    id: string;
    name: string;
    price: number;
    resourceType: string;
  }): void {
    setMode({ type: 'clone', data: item });
  }

  function handleCloseGroupResourceForm() {
    setMode({ type: 'default' });
  }

  return (
    <>
      <PageWrapper {...props}>
        <Page className="Groups absolute flex items-stretch">
          <MenuBar {...props} property={activeProperty} />
          <div className="SideMenu bg-forest-darkest flex flex-col">
            <div className="bg-white flex flex-col items-center">
              <BrandLogo logo={activeProperty.brand.logo} />
              <PropertyBanner {...props} property={activeProperty} swappable />
            </div>
            <div className={`flex flex-col relative items-stretch p-4 bg-forest-darker`}>
              <div className="grid gap-2">
                {canCreateOrEditGroupResources(user, serverInfo) && (
                  <BubbleButton
                    variant="lime"
                    className="text-left"
                    block
                    icon="plus"
                    onClick={() => {
                      setMode({ type: 'create' });
                    }}
                  >
                    New Resource
                  </BubbleButton>
                )}
              </div>
            </div>
            <BubbleAccordion
              variant="forest"
              className="flex-grow flex flex-col border-t border-forest-darkest"
              labels={['Filters']}
              open={[0]}
              locked={[0]}
            >
              <div className="grid gap-4">
                <div className="dropdown-inline-fix sticky top-0 z-10 bg-forest-darkest p-4 -mt-4">
                  <BubbleFormGroup
                    label="Resource Types"
                    labelClassName="text-white-i"
                    className="px-4"
                  >
                    <BubbleTypeahead
                      block
                      variant="forest"
                      placeholder="Filter types shown..."
                      menuClassName="max-h-80 overflow-y-auto"
                      value={filters.selectedTypes}
                      options={resourceType}
                      onAdd={v =>
                        setFilters({
                          ...filters,
                          selectedTypes: [...filters.selectedTypes, v],
                        })
                      }
                      onRemove={v =>
                        setFilters({
                          ...filters,
                          selectedTypes: filters.selectedTypes.filter(v2 => v2.key !== v.key),
                        })
                      }
                      onClearAll={() => setFilters({ ...filters, selectedTypes: [] })}
                    />
                  </BubbleFormGroup>
                  <BubbleFormGroup labelClassName="text-white-i" className="px-4">
                    <div className="text-white flex relative px-4">
                      <BubbleInput
                        type="checkbox"
                        variant="forest"
                        className="mr-1"
                        inputClassName="stretched-link"
                        checked={showOnlyArchived}
                        onValue={checked => {
                          setShowOnlyArchived(checked);
                        }}
                      />
                      <span>Show Only Archived</span>
                    </div>
                  </BubbleFormGroup>
                </div>
              </div>
            </BubbleAccordion>
          </div>
          <div className="GroupsContent bg-blue-lightest overflow-y-auto flex-grow">
            <div className="GroupsHeader align-with-logo flex items-end px-5 sticky top-0 left-0 z-10 bg-blue-lightest">
              <div className="mb-3 w-full flex items-end px-3">
                <GroupsNavigation active="resources"></GroupsNavigation>
              </div>
            </div>

            <div className="GroupsBody mx-5 content-minus-logo min-w-900">
              <BubbleOverlay loading={loading}>
                <BubbleTable
                  responsive
                  striped
                  headVariant="forest"
                  keyExtractor={item => item.id}
                  fields={tableFields}
                  items={filteredResources}
                  stickyHeader={130}
                  templates={tableTemplates}
                  showPretitle
                  pretitleClassName="p-0-i"
                  pretitleRender={() => (
                    <div className="px-4 pt-4 pb-1 flex items-center">
                      <div className="flex-grow">
                        <BubbleInput
                          variant="forest"
                          inputClassName="outline-none-i"
                          type="text"
                          block
                          value={search}
                          onValue={setSearch}
                          placeholder="Search..."
                        />
                      </div>
                    </div>
                  )}
                />
              </BubbleOverlay>
              <div className="mt-3">&nbsp;</div>
            </div>
          </div>
          {mode.type !== 'default' && (
            <BubbleDrawer hideTools containerClassName="bg-eggshell">
              <GroupResourceForm
                types={resourceData.resourceTypes}
                mode={mode}
                onClose={handleCloseGroupResourceForm}
                onSubmitGroupResource={refresh}
              />
            </BubbleDrawer>
          )}
        </Page>
      </PageWrapper>
    </>
  );
}
